import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const EditOffer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  const [formData, setFormData] = useState({
    specialName: '',
    title: '',
    validUntil: '',
    sortDescription: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadOfferData();
  }, [id]);

  const loadOfferData = async () => {
    try {
      const response = await apiService.get(`editOffer/${id}`);
      const offer = response.data.data;
      
      setFormData({
        specialName: offer.specialName || '',
        title: offer.title || '',
        validUntil: offer.validUntil ? offer.validUntil.split('T')[0] : '',
        sortDescription: offer.sortDescription || ''
      });

      if (offer.image) {
        setImagePreview(`${mediaUrl}${offer.image}`);
      }
    } catch (error) {
      toast.error('Failed to load offer data');
      console.error(error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 1 * 1024 * 1024; // 1MB
      if (file.size > maxSize) {
        toast.error('File size must be 1MB or less.');
        e.target.value = '';
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    document.getElementById('pic').value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('id', id);
      submitData.append('specialName', formData.specialName);
      submitData.append('title', formData.title);
      submitData.append('validUntil', formData.validUntil);
      submitData.append('sortDescription', formData.sortDescription);
      
      if (imageFile) {
        submitData.append('image', imageFile);
      }

      const response = await apiService.postFormData('updateOffer', submitData);
      
      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Offer updated successfully');
        navigate('/view-offers');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-0">
        <div className="mb-3">
          <nav className="flex text-sm text-gray-600">
            <span className="cursor-pointer hover:text-[#009ef7]" onClick={() => navigate('/dashboard')}>Home</span>
            <span className="mx-2">/</span>
            <span className="cursor-pointer hover:text-[#009ef7]" onClick={() => navigate('/offers')}>Offers</span>
            <span className="mx-2">/</span>
            <span className="text-gray-900">Edit Offer</span>
          </nav>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="grid grid-cols-12 gap-6">
              {/* Image Upload Section */}
              <div className="col-span-12 md:col-span-5">
                <label className="block text-sm font-semibold text-gray-700 mb-4">Image</label>
                <div className="relative">
                  <div 
                    className="w-full h-60 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden"
                    style={{
                      backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {!imagePreview && (
                      <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-500">Upload Image</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="absolute bottom-2 right-2 flex gap-2">
                    <label className="w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50">
                      <i className="bi bi-pencil text-sm" style={{ color: '#009ef7' }}></i>
                      <input
                        type="file"
                        id="pic"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                    {imagePreview && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50"
                      >
                        <i className="bi bi-x text-lg text-gray-600"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Fields Section */}
              <div className="col-span-12 md:col-span-7">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Special Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="specialName"
                      value={formData.specialName}
                      onChange={handleInputChange}
                      placeholder="Enter special name"
                      required
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{ focusRingColor: '#009ef7' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter title"
                      required
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{ focusRingColor: '#009ef7' }}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valid Until</label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ focusRingColor: '#009ef7' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
                  <textarea
                    name="sortDescription"
                    value={formData.sortDescription}
                    onChange={handleInputChange}
                    placeholder="Enter short description"
                    rows="3"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none"
                    style={{ focusRingColor: '#009ef7' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/view-offers')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#009ef7' }}
            >
              {loading ? (
                <>
                  <span>Please wait...</span>
                  <span className="ml-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                </>
              ) : (
                'Update'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOffer;
